import React, { useContext } from 'react';
import styled, { useTheme } from 'styled-components';
import PropTypes from 'prop-types';

import { CartContext } from '@rsc/contexts/CartContext';
import { LocaleContext } from '@rsc/contexts/LocaleContext';
import { ProductContainerContext } from '@rsc/contexts/ProductContainerContext';

import { FETCH_PRODUCT_UPDATE_QUERY } from '@rsc/contexts/cartQueries';

import { pushToDataLayer } from '@rsc/utils';

import styles from '@rsc/ui-components/styles';
import Button from '@rsc/ui-components/button/Button';
import { useLocalizedNavigation } from '@rsc/gatsby-localization-helpers';
import breakpoints from '../../../../breakpoints';

// eslint-disable-next-line import/no-cycle
import BlockBuilder from '../../../BlockBuilder';

const RecommendationsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: ${styles.spacing['spacing-md']};
  flex-direction: column;

  @media (min-width: ${breakpoints.md}) {
    flex-direction: row;
  }
`;

const RecommendationCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  gap: ${styles.spacing['spacing-md']};
  background-color: ${({ theme }) =>
    theme.quizCard.recommendationBackgroundColor};
  padding: ${styles.spacing['spacing-1xl']} ${styles.spacing['spacing-lg']}
    ${styles.spacing['spacing-lg']} ${styles.spacing['spacing-lg']};

  ul {
    padding-inline-start: ${styles.spacing['spacing-md']};
  }

  @media (min-width: ${breakpoints.md}) {
    display: flex;
    flex-direction: column;
    flex: 1 1 30%;

    h3,
    ul,
    p {
      margin: 0;
    }

    ul,
    .productPrice,
    p {
      flex: 1;
    }
  }
`;

const MostPopularBadge = styled.div`
  margin-left: -${styles.spacing['spacing-lg']};
  margin-top: -${styles.spacing['spacing-1xl']};
  margin-bottom: ${styles.spacing['spacing-lg']};
  display: block;
  width: fit-content;
  color: white;
  background-color: ${({ theme }) => theme.colors.brandPrimary};
  border-bottom-right-radius: ${styles.spacing['spacing-2xs']};
  padding: ${styles.spacing['spacing-2xs']} ${styles.spacing['spacing-sm']};

  p {
    margin: 0;
  }

  @media (min-width: ${breakpoints.md}) {
    margin-bottom: 0;
  }
`;

const ProductPrice = styled.div`
  text-align: center;
  color: ${({ theme }) => theme.colors.brandPrimary};

  .sale-price {
    color: ${({ theme }) => theme.colors.onSaleRed};
  }
`;

const StrikeThrough = styled.span`
  display: block;
  text-decoration: line-through;
  font-size: ${styles.fontSize.sm};
`;

const StyledImage = styled.img`
  display: block;
  width: 100%;
  height: auto;
  object-fit: cover;
  aspect-ratio: 3/2;
  max-width: 100%;
  margin-bottom: ${styles.spacing['spacing-md']};
`;

const hasMatchingOption = (option, answers) =>
  Object.values(answers).some(ans => ans.option === option._id);

const handleORResult = (result, filteredRecommendations, answers) => {
  const hasMatchingAnswer = result.quizAnswers.some(quizAnswer =>
    quizAnswer.answers.some(option => hasMatchingOption(option, answers))
  );

  if (hasMatchingAnswer && result.recommendations?.length > 0) {
    filteredRecommendations.push(...result.recommendations);
  }
};

const handleANDResult = (result, filteredRecommendations, answers) => {
  const allAnswersMatch = result.quizAnswers.every(quizAnswer =>
    quizAnswer.answers.some(option => hasMatchingOption(option, answers))
  );

  if (allAnswersMatch && result.recommendations?.length > 0) {
    filteredRecommendations.push(...result.recommendations);
  }
};

const filterRecommendations = (results, answers) => {
  const filteredRecommendations = [];

  results.forEach(result => {
    if (result.operator === 'OR') {
      handleORResult(result, filteredRecommendations, answers);
    } else if (result.operator === 'AND') {
      handleANDResult(result, filteredRecommendations, answers);
    }
  });

  return filteredRecommendations;
};

const getRecommendations = (results, answers, hasCompanyOwnedDealer) => {
  const filteredRecommendations = filterRecommendations(results, answers);

  return filteredRecommendations.filter(recommendation => {
    const hasCompanyOwnedDealerFlag = !!hasCompanyOwnedDealer;
    const isDynamicDealerCard = !!recommendation.dynamicDealerCard;
    const isCompanyOwnedDealer = !!recommendation.companyOwnedDealer;

    if (hasCompanyOwnedDealerFlag) {
      return isDynamicDealerCard ? isCompanyOwnedDealer : true;
    }
    return isDynamicDealerCard ? !isCompanyOwnedDealer : true;
  });
};

function Recommendations({
  results,
  answers,
  hasCompanyOwnedDealer,
  getInternalLink,
  noResultsFallback,
}) {
  const theme = useTheme();
  const localeContext = useContext(LocaleContext);
  const { activeVariant, product } = useContext(ProductContainerContext);
  const { shopifyClient, formatPrice, addToCart } = useContext(CartContext);
  const currency = localeContext?.currency || 'USD';

  const navigate = useLocalizedNavigation();

  const addProductToCart = async productCard => {
    const productId =
      currency === 'CAD'
        ? productCard?.productVariant?.canadaStore?.productGid
        : productCard.product.store.gid;

    const variantId =
      currency === 'CAD'
        ? productCard?.productVariant?.canadaStore?.gid
        : productCard?.productVariant?.store?.gid;

    const { product: shopifyProduct } = await shopifyClient.request(
      FETCH_PRODUCT_UPDATE_QUERY,
      {
        id: productId,
      }
    );

    await addToCart({
      activeVariant,
      product,
      quantity: 1,
      selectedSellingPlan: {
        id: shopifyProduct?.sellingPlanGroups?.nodes?.[0]?.sellingPlans
          ?.nodes?.[0]?.id,
      },
      sellingPlanGroups: shopifyProduct?.sellingPlanGroups?.nodes || [],
      variantId,
      variantMetafields: productCard?.productVariant?.store?.metafields,
    });

    const lineItemPrice =
      (currency === 'CAD'
        ? productCard?.productVariant?.canadaStore?.price
        : productCard?.productVariant?.store?.price) || 0;

    pushToDataLayer('add_to_cart', {
      ecommerce: {
        currency,
        value: Number(lineItemPrice).toFixed(2),
      },
      items: [
        {
          variant: {
            sku: productCard?.productVariant?.store?.sku,
            title: productCard?.productVariant?.store?.title,
            price: {
              amount: Number(lineItemPrice).toFixed(2).toString(),
            },
          },
          product: {
            title: productCard?.product?.store?.title,
            productType: productCard?.product?.store?.productType,
            vendor: productCard?.product?.store?.vendor,
          },
          quantity: 1,
        },
      ],
    });
  };

  const startCheckout = async () => {};

  const startNewCheckout = async () => {};

  const handleSelectRecommendation = async (event, recommendation) => {
    event.preventDefault();

    const ctaAction = recommendation?.ctaAction || 'Navigate to a Page';

    if (ctaAction === 'Add To Cart') {
      const productCard = {
        product: {
          store: recommendation.reference.store,
        },
        productVariant: {
          store: recommendation.reference.store.variants[0]?.store,
          canadaStore: recommendation.reference.store.variants[0]?.canadaStore,
        },
      };

      await addProductToCart(productCard);
      return;
    }

    if (ctaAction === 'Start Checkout') {
      await startCheckout();
      return;
    }

    if (ctaAction === 'Start New Checkout') {
      await startNewCheckout();
      return;
    }

    const documentSlug =
      recommendation?.reference?.slug?.current ||
      recommendation?.reference?.store?.slug?.current;

    let slug = documentSlug.startsWith('/') ? documentSlug : `/${documentSlug}`;

    if (recommendation?.reference?._type === 'product') {
      slug = `/product${slug}`;
    }

    let link = getInternalLink({ slug });

    if (
      recommendation?.reference?._type === 'product' &&
      recommendation?.variantReference?.store?.sku
    ) {
      link = `${link}?sku=${recommendation.variantReference.store.sku}`;
    }

    if (ctaAction === 'Open Page in a new tab') {
      window.open(link, {
        target: '_blank',
      });
      return;
    }

    navigate(link);
  };

  const getRecommendationImage = recommendation => {
    if (recommendation?.enableImage) {
      // Custom image
      if (recommendation?.customImage?.asset?.url) {
        return {
          src: recommendation.customImage.asset.url,
          alt: recommendation?.customImage?.alt || recommendation?.headline,
        };
      }

      // Frontmatter image on sanity documents
      if (recommendation?.reference?.image?.asset?.url) {
        return {
          src: recommendation.reference.image.asset.url,
          alt: recommendation.reference.image?.alt || recommendation?.headline,
        };
      }

      // Check if we have variant selected and if variant has image
      if (recommendation?.variantReference?.store?.previewImageUrl) {
        return {
          src: recommendation.variantReference.store.previewImageUrl,
          alt:
            recommendation.variantReference.store?.title ||
            recommendation?.headline,
        };
      }

      // Product Image
      if (recommendation?.reference?.store?.previewImageUrl) {
        return {
          src: recommendation.reference.store.previewImageUrl,
          alt:
            recommendation.reference.store?.title || recommendation?.headline,
        };
      }

      // Fallback Image
      return {
        src: theme.images.pagePreviewFallbackImage,
        alt: recommendation?.headline || 'Brand Page',
      };
    }

    return null;
  };

  const recommendations = getRecommendations(
    results,
    answers,
    hasCompanyOwnedDealer
  );

  return (
    <RecommendationsContainer>
      {recommendations?.length > 0 ? (
        recommendations?.map(recommendation => {
          const recommendationVariant =
            recommendation?.variantReference ||
            recommendation?.reference?.store?.variants?.[0];

          const salePrice = recommendationVariant?.store?.compareAtPrice;
          const price = recommendationVariant?.store?.price;
          const recommendationImage = getRecommendationImage(recommendation);

          return (
            <RecommendationCard
              key={`recommendation-${recommendation.reference._id}`}
            >
              {recommendation?.mostPopular && (
                <MostPopularBadge className="mostPopular">
                  <p>{recommendation?.badgeLabel || 'Most Popular'}</p>
                </MostPopularBadge>
              )}
              {recommendationImage?.src && (
                <StyledImage
                  src={recommendationImage.src}
                  alt={recommendationImage.alt}
                />
              )}
              {(recommendation?.headline ||
                recommendation?.reference?.title ||
                recommendation?.reference?.store?.title) && (
                <h3>
                  {recommendation?.headline ||
                    recommendation?.reference?.title ||
                    recommendation?.reference?.store?.title}
                </h3>
              )}

              {(recommendation?.features?.length > 0 ||
                recommendation?.reference?.features?.length) > 0 && (
                <ul>
                  {recommendation?.features?.length > 0 &&
                    recommendation.features.map(feature => (
                      <li key={`feature-${feature}`}>{feature}</li>
                    ))}

                  {!recommendation?.features?.length &&
                    recommendation?.reference?.features?.length > 0 &&
                    recommendation.reference.features.map(feature => (
                      <li key={`feature-${feature}`}>{feature}</li>
                    ))}
                </ul>
              )}

              {recommendation?.showPrice &&
                (recommendation?.customPrice || price || salePrice) && (
                  <ProductPrice className="productPrice">
                    {recommendation?.customPrice ? (
                      <p>
                        <strong>{recommendation.customPrice}</strong>
                      </p>
                    ) : (
                      <p>
                        {recommendation?.pricePrefix || 'Online Price'}
                        <br />
                        {salePrice ? (
                          <>
                            <StrikeThrough>
                              {formatPrice(salePrice)}
                            </StrikeThrough>
                            <strong className="sale-price">
                              SALE{' '}
                              {formatPrice(recommendationVariant.store.price)}
                            </strong>
                          </>
                        ) : (
                          <strong>
                            {formatPrice(recommendationVariant.store.price)}
                          </strong>
                        )}
                      </p>
                    )}
                  </ProductPrice>
                )}

              {recommendation?.enableDescription && (
                <p>
                  {recommendation?.customDescription ||
                    recommendation?.reference?.description}
                </p>
              )}

              {recommendation?.body?.text?.length > 0 && (
                <BlockBuilder
                  blocks={recommendation.body.text}
                  getInternalLink={getInternalLink}
                  Button={Button}
                />
              )}

              {recommendation?.enableCardCta && (
                <Button
                  onClick={event =>
                    handleSelectRecommendation(event, recommendation)
                  }
                  fullWidth
                >
                  {recommendation?.ctaLabel || 'Learn More'}
                </Button>
              )}
            </RecommendationCard>
          );
        })
      ) : (
        <BlockBuilder blocks={noResultsFallback?.text} />
      )}
    </RecommendationsContainer>
  );
}

Recommendations.propTypes = {
  results: PropTypes.arrayOf(PropTypes.shape({})),
  answers: PropTypes.shape({}),
  hasCompanyOwnedDealer: PropTypes.bool,
  getInternalLink: PropTypes.func,
  noResultsFallback: PropTypes.shape({
    text: PropTypes.arrayOf(PropTypes.shape({})),
  }),
};

Recommendations.defaultProps = {
  results: [],
  answers: {},
  hasCompanyOwnedDealer: false,
  getInternalLink: () => {},
  noResultsFallback: {},
};

export default Recommendations;
